'use client';

import type { FC } from 'react';
import type { IconName } from '@/components/entities/Sprite/Icon';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import debounce from 'lodash.debounce';
import { useTranslations } from 'next-intl';
import { notFound } from 'next/navigation';
import { Suspense, useCallback, useState } from 'react';
import EmptyGamesState from '@/components/entities/games/EmptyGamesState';
import GameCard from '@/components/entities/games/GameCard';
import GameSkeleton from '@/components/entities/games/GameCard/components/GameSkeleton';
import { GamesSearchBar } from '@/components/entities/games/GamesSearchBar';
import { Icon } from '@/components/entities/Sprite';
import Pagination from '@/components/shared/Pagination';
import { SkeletonParent } from '@/components/ui/Skeleton/skeleton';
import { CATEGORY_ICONS } from '@/consts/CategoryIcons';
import { SlotsFilledAliases } from '@/consts/seo/SlotsAliases';
import { AppRoutes } from '@/i18n/constants';
import { isStaging } from '@/libs/getEnvironment';
import { queryCategoryById } from '@/queries/categories/categories.queries';
import { gameQueryOptions } from '@/queries/games/games.queries';
import { PUBLIC_QUERY_STALE_TIME } from '@/queries/queryOptions.helpers';
import { withSearchQuery } from '@/utils/paginationHref.utils';
import { capitalizeWords } from '@/utils/texts.utils';

interface CategoryViewProps {
  categoryId: string;
  isAllGamesCategory?: boolean;
  page: number;
}

const CategoryView: FC<CategoryViewProps> = ({ categoryId, isAllGamesCategory, page }) => {
  const t = useTranslations('lobby.filters');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const isStagingEnv = isStaging();

  const debouncedSetQuery = useCallback(
    debounce((val: string) => {
      setDebouncedQuery(val);
    }, 500),
    [],
  );

  const handleSearchChange = (value: string) => {
    debouncedSetQuery(value);
  };

  const clearSearch = () => {
    debouncedSetQuery('');
    setDebouncedQuery('');
  };

  const { data: category, isFetching: isCategoryFetching } = useSuspenseQuery(queryCategoryById(categoryId));

  if (!category) {
    notFound();
  }

  const { data: gamesData, isFetching: isGamesFetching } = useQuery(
    gameQueryOptions.listByCategory({
      page,
      categoryId,
      searchValue: debouncedQuery,
      options: { staleTime: PUBLIC_QUERY_STALE_TIME },
    }),
  );

  const isGamesEmpty = gamesData?.items && gamesData.items.length === 0;
  const showSkeletons = isGamesFetching;

  return (
    <>
      <div className="flex items-center justify-between gap-4 relative mb-4.5">
        {!isCategoryFetching
          ? (
              <h1 className="text-white paragraph-l-medium flex items-center gap-1">
                <Icon
                  name={CATEGORY_ICONS[categoryId] as IconName}
                  className="size-6 text-white"
                />
                {capitalizeWords(category?.name ?? '')}
              </h1>
            )
          : (
              <SkeletonParent className="h-7 w-75" />
            )}

        <Suspense
          fallback={(
            <div className="absolute right-0 w-full flex justify-end">
              <div className="transition-all duration-300 size-10 md:w-62.5 rounded-[6.25rem] bg-c900 animate-pulse" />
            </div>
          )}
        >
          <GamesSearchBar
            placeholder={t('search-placeholder')}
            onChange={handleSearchChange}
            onClear={clearSearch}
          />
        </Suspense>

      </div>

      {showSkeletons
        ? (
            <div className="grid  mt-6 grid-cols-3 md:grid-cols-4 lg:flex lg:flex-wrap gap-4 lg:gap-x-3.5 md:gap-5 w-full">
              {Array.from({ length: 20 }).map((_, idx) => (
                <GameSkeleton key={idx} />
              ))}
            </div>
          )
        : (
            <>
              {isGamesEmpty && gamesData?.items && (
                <EmptyGamesState isSearch={!!debouncedQuery} />
              )}

              {!isGamesEmpty && (
                <div className="grid mt-6 grid-cols-3 md:grid-cols-4 lg:flex lg:flex-wrap gap-4 lg:gap-x-3.5 md:gap-5 w-full">
                  {gamesData?.items.map((game) => {
                    const isFilledAlias = SlotsFilledAliases.includes(game.alias);
                    const shouldUseSlotsSlug = Boolean(isAllGamesCategory) && (isStagingEnv || isFilledAlias);

                    return (
                      <GameCard
                        key={game.id}
                        id={game.id}
                        name={game.name}
                        cover={game.cover}
                        likeButtonPosition="compactLg"
                        likeButtonIconSize="compactLg"
                        path={shouldUseSlotsSlug ? AppRoutes.slotsSlug(game.alias) : AppRoutes.gamesId(game.id)}
                      />
                    );
                  })}
                </div>
              )}
            </>
          )}

      {!isGamesEmpty && gamesData?.totalPages && gamesData.totalPages > 1 && (
        <Pagination
          variant="link"
          pageNumber={page}
          totalItems={gamesData?.totalItems ?? 0}
          totalPages={gamesData?.totalPages ?? 0}
          getHrefAction={nextPage => withSearchQuery(AppRoutes.categoryIdPaged(categoryId, nextPage), debouncedQuery, 'search')}
        />
      )}
    </>
  );
};

export default CategoryView;
